import { Modal, Typography, withStyles } from '@material-ui/core';
import { Add, SearchOutlined, Waves } from '@material-ui/icons';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import PF2MButton from '~/components/PF2MButton';
import PF2MEquipmentScheduleModal from '~/components/PF2MEquipmentScheduleModal';
import PF2MKanban from '~/components/PF2MKanban/PF2MKanban';
import RainModeModal from '~/components/RainModeModal';
import Header from '~/pages/Dispatch/Header';
import {
  exportEquipmentsSchedules,
  getEquipmentsSchedules,
  getEquipmentsSchedulesTemplate,
  importEquipmentsSchedules,
  setEquipmentsScheduleValue,
  updateEquipmentsSchedules,
} from '~/store/dispatch/actions';
import EquipmentProgramsFilterModal from '../EquipmentProgramsFilterModal';
import EquipmentProgramsKanbanModal from '../EquipmentProgramsKanbanModal';
import styles from '../styles';
import ReloadQueue, { priority } from '../../ReloadQueue';
import {
  getCodeGroups,
  getCodes,
  getCodeTypes,
  getElements,
  getEquipmentGroups,
  getEquipments,
  getEquipmentTypes,
  getSubElements,
  getMaterials,
} from '~/store/nref/actions';
import useTabletConfiguration from '~/services/tabletConfiguration';

const EquipmentProgramsTab = ({ classes }) => {
  const [modalOpen, setModalOpen] = useState(0);
  const [kanbanModalInfo, setKanbanModalInfo] = useState([]);

  const equipmentsSchedules = useSelector(state => state.dispatch.equipmentsSchedules);
  const currentFilter = useSelector(state => state.dispatch.currentEquipmentSchedulesFilter);
  const equipmentType = useSelector(
    state => state.nref.equipmentTypes.find(r => r.id === currentFilter.equipType),
  );
  const equipmentGroup = useSelector(
    state => state.nref.equipmentGroups.find(r => r.id === currentFilter.equipGroup),
  );
  const equipment = useSelector(
    state => state.nref.equipments.find(r => r.id === currentFilter.equip),
  );
  const element = useSelector(
    state => state.nref.elements.find(r => r.id === currentFilter.element),
  );
  const subElement = useSelector(
    state => state.nref.subElements.find(r => r.id === currentFilter.subElement),
  );
  const { value: dispatchUpdateTime } = useTabletConfiguration('dispatch_update_time');
  const dataValue = { fontWeight: 'bold', marginLeft: 5 };

  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const [showAlert, setShowAlert] = useState(false);

  const reloadList = [
    {
      name: 'getEquipmentsSchedules',
      action: () => (reduxDispatch, getState) => {
        const { currentEquipmentSchedulesFilter } = getState().dispatch;
        return reduxDispatch(getEquipmentsSchedules({
          ...currentEquipmentSchedulesFilter,
          status_id: 0,
        }));
      },
    },
    { name: 'getEquipmentTypes', action: getEquipmentTypes },
    { name: 'getCodeTypes', action: getCodeTypes },
    { name: 'getCodeGroups', action: getCodeGroups },
    { name: 'getElements', action: getElements },
    { name: 'getSubElements', action: getSubElements },
    { name: 'getMaterials', action: getMaterials },
    { name: 'getEquipments', action: getEquipments },
    { name: 'getEquipmentGroups', action: getEquipmentGroups },
    { name: 'getCodes', action: getCodes },
  ];

  useEffect(() => {
    if (dispatchUpdateTime !== undefined) {
      ReloadQueue.setUpdateTime(Number(dispatchUpdateTime), dispatch);
    }

    ReloadQueue.clearReloadList(priority.MEDIUM, dispatch);
    ReloadQueue.addToReloadList(dispatch, reloadList, priority.MEDIUM);

    return () => (
      ReloadQueue.stopReload()
    );
  }, [dispatch, dispatchUpdateTime]);

  const handleKanbanCardModal = (c) => {
    setKanbanModalInfo(c);
    setModalOpen(3);
  };

  const stages = [
    { title: translate('common:NotStarted'), id: 1 },
    { title: translate('common:Disapproved'), id: 5, style: 'danger' },
    { title: translate('common:Paused'), id: 6 },
    { title: translate('common:InProgress'), id: 2, style: 'highlight' },
    { title: translate('common:OnApproval'), id: 3 },
    { title: translate('common:Finalized'), id: 4, style: 'disabled' },
  ];

  const content = {
    title: 'group_equipament_links_name',
    startDate: 'start_date',
    startTime: 'start_time',
    codeGroup: 'code_groups_name',
    codeType: 'code_type_name',
    equipType: 'equipament_type_name',
    equipGroup: 'equipament_groups_name',
  };

  const renderHeader = () => (
    <div style={{ display: 'flex', justifyContent: 'flex-start', paddingBottom: 20 }}>
      <PF2MButton
        type="button"
        size="medium"
        onClick={() => setModalOpen(2)}
      >
        <SearchOutlined className={classes.icon} />
        {translate('common:Filter')}
      </PF2MButton>
      <PF2MButton
        type="button"
        size="medium"
        onClick={() => setModalOpen(1)}
      >
        <Add className={classes.icon} />
        {translate('common:NewSchedule')}
      </PF2MButton>
      <Header
        getTemplateAction={() => dispatch(getEquipmentsSchedulesTemplate())}
        importAction={file => dispatch(importEquipmentsSchedules(file, currentFilter))}
        exportAction={() => dispatch(exportEquipmentsSchedules())}
        messageStyle={{ paddingTop: 10, paddingLeft: 10 }}
        actionButton={{
          label: translate('common:RainMode'),
          icon: <Waves className={classes.icon} />,
          onClick: () => setModalOpen(4),

        }}
      />
    </div>
  );

  return (
    <div className={classes.tabContainer}>
      {renderHeader()}
      <div style={{ paddingBottom: 16 }}>
        <Typography variant="caption">{`${translate('common:ResultsFor')}:`}</Typography>
        <Typography>
          <span style={dataValue}>
            {`${currentFilter.startDate.format(translate('date:DateFormat'))} - ${currentFilter.endDate.format(translate('date:DateFormat'))}`}
          </span>
          <span>{`, ${translate('common:EquipmentType')}`}</span>
          <span style={dataValue}>{equipmentType ? equipmentType.name : translate('common:All')}</span>
          <span>{`, ${translate('common:EquipmentGroup')}`}</span>
          <span style={dataValue}>{equipmentGroup ? equipmentGroup.name : translate('common:All')}</span>
          <span>{`, ${translate('common:Equipment')}`}</span>
          <span style={dataValue}>{equipment ? equipment.name : translate('common:All')}</span>
          <span>{`, ${translate('common:Element')}`}</span>
          <span style={dataValue}>{element ? element.name : translate('common:All')}</span>
          <span>{`, ${translate('common:SubElement')}`}</span>
          <span style={dataValue}>{subElement ? subElement.name : translate('common:All')}</span>
        </Typography>
      </div>
      <PF2MKanban
        data={equipmentsSchedules}
        stageColumn={'status_id'}
        stages={stages}
        content={content}
        onClickItem={handleKanbanCardModal}
      />
      <Modal
        open={modalOpen === 4}
        onClose={(_, reason) => reason === 'backdropClick' && setModalOpen(0)}
        className={classes.modal}
      >
        <div>
          <RainModeModal setModalOpen={setModalOpen} />
        </div>
      </Modal>
      <Modal
        open={modalOpen === 3}
        onClose={(_, reason) => reason === 'backdropClick' && setModalOpen(0)}
        className={classes.modal}
      >
        <div>
          <EquipmentProgramsKanbanModal
            data={kanbanModalInfo}
            setModalOpen={setModalOpen}
            onRemove={(data) => { dispatch(setEquipmentsScheduleValue({ ...data, active: 0 })); }}
            onChange={(data, value, field) => {
              dispatch(setEquipmentsScheduleValue({ ...data, [field]: value }));
            }}
          />
        </div>
      </Modal>
      <Modal
        open={modalOpen === 2}
        onClose={(_, reason) => reason === 'backdropClick' && setModalOpen(0)}
        className={classes.modal}
      >
        <div>
          <EquipmentProgramsFilterModal setModalOpen={setModalOpen} />
        </div>
      </Modal>
      <Modal
        open={modalOpen === 1}
        onClose={(_, reason) => reason === 'backdropClick' && setModalOpen(0)}
        className={classes.modal}
      >
        <div>
          <PF2MEquipmentScheduleModal
            setModalOpen={setModalOpen}
            settings={{ skipBulkUpdate: true }}
          />
        </div>
      </Modal>
      <PF2MAlertDialog
        confirmText={translate('common:Yes')}
        description={translate('validation:WarningScheduleChange')}
        open={showAlert}
        onClose={() => setShowAlert(false)}
        onConfirm={() => {
          setShowAlert(false);
          dispatch(updateEquipmentsSchedules(equipmentsSchedules, currentFilter));
        }}
      />
    </div>
  );
};

EquipmentProgramsTab.propTypes = {
  classes: PropTypes.object.isRequired,
};

EquipmentProgramsTab.defaultProps = {};

export default withStyles(styles)(EquipmentProgramsTab);
